import { useReadContracts, useBalance } from 'wagmi'
import { Address, formatUnits } from 'viem'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { erc20Abi } from 'viem'
import { WHYPE_ADDRESS_HYPEREVM } from '../nativeHype'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { getSubAccountAddress, FALLBACK_SUBACCOUNT_RANGE } from '../shared/subAccounts'
import { useSubaccountStore } from '../subaccounts'
import { useMemo } from 'react'

/** Balance info for a specific sub-account */
export interface SubAccountBalance {
  subAccountId: number
  subAccountAddress: Address
  vaultBalance: bigint
  vaultBalanceFormatted: string
  depositAssets: bigint
  depositAssetsFormatted: string
}

export interface WalletBalanceBreakdown {
  nativeBalance: bigint
  nativeBalanceFormatted: string
  wrappedBalance: bigint
  wrappedBalanceFormatted: string
}

export interface VaultUserBalance {
  vaultAddress: Address
  vaultBalance: bigint
  vaultBalanceFormatted: string
  walletBalance: bigint
  walletBalanceFormatted: string
  sharePrice: bigint
  depositAssets: bigint
  depositAssetsFormatted: string
  debt: bigint
  debtFormatted: string
  /** The first subaccount that has a non-zero vault balance, or undefined if none */
  firstSubAccountWithBalance: number | undefined
  /** All sub-accounts with non-zero balances */
  subAccountBalances: SubAccountBalance[]
  /** For WHYPE vaults: breakdown of native HYPE vs wrapped WHYPE wallet balances */
  walletBalanceBreakdown?: WalletBalanceBreakdown
}

export interface UseVaultUserBalancesParams {
  vaultAddresses: Address[]
  underlyingAssets?: (Address | undefined)[]
  decimals?: (number | undefined)[]
  userAddress?: Address
  chainId: number
  enabled?: boolean
  includeDebt?: boolean
  /** When true, queries all subaccounts (0-9) and aggregates their balances */
  includeSubAccounts?: boolean
}

export interface UseVaultUserBalancesResult {
  balances: Map<Address, VaultUserBalance>
  isPending: boolean
}

/**
 * Shared hook for fetching user balances across multiple vaults
 * Used by both listing pages (useELend, useEVaults) and position hooks (useELendPositions, etc.)
 */
export function useVaultUserBalances({
  vaultAddresses,
  underlyingAssets,
  decimals,
  userAddress,
  chainId,
  enabled = true,
  includeDebt = false,
  includeSubAccounts = false,
}: UseVaultUserBalancesParams): UseVaultUserBalancesResult {
  // Get active subaccount IDs from the global discovery cache
  const { activeSubaccountIds } = useSubaccountStore()

  // Generate subaccount addresses if needed
  // Include fallback range (0-15) for safety, plus any higher active subaccounts from discovery
  const subAccountAddresses = useMemo(() => {
    if (!userAddress || !includeSubAccounts) return []
    const fallbackIds = Array.from({ length: FALLBACK_SUBACCOUNT_RANGE }, (_, i) => i)
    const idsToQuery = [...new Set([...fallbackIds, ...activeSubaccountIds])]
    return idsToQuery.map((id) => ({
      subAccountId: id,
      subAccountAddress: getSubAccountAddress(userAddress, id),
    }))
  }, [userAddress, includeSubAccounts, activeSubaccountIds])

  // Fetch native HYPE balance for WHYPE vaults
  const { data: nativeBalance } = useBalance({
    address: userAddress,
    chainId,
    query: {
      enabled: !!userAddress && chainId === E_LEND_CHAIN_ID && enabled,
      refetchInterval: 60_000,
    },
  })

  // Build contracts array for batch call
  const contracts = useMemo(() => {
    if (!userAddress) return []

    // Determine which addresses to query for vault balances
    const addressesToQuery = includeSubAccounts
      ? subAccountAddresses
      : [{ subAccountId: 0, subAccountAddress: userAddress }]

    return vaultAddresses.flatMap((vaultAddress, idx) => {
      const underlyingAsset = underlyingAssets?.[idx]

      // Query vault balances for all relevant addresses (main account or all subaccounts)
      const vaultBalanceContracts = addressesToQuery.flatMap(({ subAccountId, subAccountAddress }, subIdx) => [
        // User's vault share balance
        {
          address: vaultAddress,
          abi: eVaultImplementationAbi,
          functionName: 'balanceOf' as const,
          args: [subAccountAddress],
          chainId,
          _idx: idx,
          _subIdx: subIdx,
          _subAccountId: subAccountId,
          _type: 'vaultBalance' as const,
        },
      ])

      // Share price (only need once per vault, not per subaccount)
      const sharePriceContract = {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'convertToAssets' as const,
        args: [BigInt(10 ** 18)],
        chainId,
        _idx: idx,
        _subIdx: 0,
        _type: 'sharePrice' as const,
      }

      // Wallet balance of underlying (only need once, always from main account)
      const walletContract = underlyingAsset
        ? [
            {
              address: underlyingAsset,
              abi: erc20Abi,
              functionName: 'balanceOf' as const,
              args: [userAddress],
              chainId,
              _idx: idx,
              _subIdx: 0,
              _type: 'walletBalance' as const,
            },
          ]
        : []

      // Debt (query for all addresses if includeSubAccounts, otherwise just main account)
      const debtContracts = includeDebt
        ? addressesToQuery.map(({ subAccountId, subAccountAddress }, subIdx) => ({
            address: vaultAddress,
            abi: eVaultImplementationAbi,
            functionName: 'debtOf' as const,
            args: [subAccountAddress],
            chainId,
            _idx: idx,
            _subIdx: subIdx,
            _subAccountId: subAccountId,
            _type: 'debt' as const,
          }))
        : []

      return [...vaultBalanceContracts, sharePriceContract, ...walletContract, ...debtContracts]
    })
  }, [userAddress, vaultAddresses, underlyingAssets, chainId, includeDebt, includeSubAccounts, subAccountAddresses])

  const { data: balanceData, isPending } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: contracts.map(({ _idx: _, _subIdx: __, _type: ___, ...contract }) => contract) as any,
    query: {
      enabled: enabled && !!userAddress && vaultAddresses.length > 0,
      staleTime: 30_000,
    },
  })

  // Build result map
  const balances = new Map<Address, VaultUserBalance>()

  if (balanceData && contracts.length > 0) {
    // Group results by vault index, aggregating vault balances and debt across subaccounts
    // Also track individual sub-account balances
    const vaultResults = new Map<
      number,
      {
        vaultBalance: bigint
        sharePrice?: bigint
        walletBalance?: bigint
        debt: bigint
        firstSubAccountWithBalance: number | undefined
        subAccountBalances: Map<number, { subAccountId: number; subAccountAddress: Address; balance: bigint }>
      }
    >()

    contracts.forEach((contract, resultIdx) => {
      const result = balanceData[resultIdx]
      if (result?.status !== 'success') return

      const idx = contract._idx
      if (!vaultResults.has(idx)) {
        vaultResults.set(idx, {
          vaultBalance: 0n,
          debt: 0n,
          firstSubAccountWithBalance: undefined,
          subAccountBalances: new Map(),
        })
      }
      const vaultResult = vaultResults.get(idx)!

      switch (contract._type) {
        case 'vaultBalance': {
          // Aggregate vault balances across all subaccounts
          const balance = result.result as bigint
          // Track the first subaccount with a non-zero balance (use actual subAccountId, not index)
          if (balance > 0n && vaultResult.firstSubAccountWithBalance === undefined) {
            vaultResult.firstSubAccountWithBalance = contract._subAccountId
          }
          vaultResult.vaultBalance = vaultResult.vaultBalance + balance

          // Track individual sub-account balance
          if (balance > 0n && contract._subAccountId !== undefined) {
            const subAccountAddr = subAccountAddresses.find((s) => s.subAccountId === contract._subAccountId)
            if (subAccountAddr) {
              vaultResult.subAccountBalances.set(contract._subAccountId, {
                subAccountId: contract._subAccountId,
                subAccountAddress: subAccountAddr.subAccountAddress,
                balance,
              })
            }
          }
          break
        }
        case 'sharePrice':
          vaultResult.sharePrice = result.result as bigint
          break
        case 'walletBalance':
          vaultResult.walletBalance = result.result as bigint
          break
        case 'debt':
          // Aggregate debt across all subaccounts
          vaultResult.debt = vaultResult.debt + (result.result as bigint)
          break
      }
    })

    // Convert to VaultUserBalance objects
    vaultResults.forEach((result, idx) => {
      const vaultAddress = vaultAddresses[idx]
      if (!vaultAddress) return

      const vaultBalance = result.vaultBalance
      const sharePrice = result.sharePrice || BigInt(10 ** 18)
      let walletBalance = result.walletBalance || 0n
      const debt = result.debt
      const depositAssets = (vaultBalance * sharePrice) / BigInt(10 ** 18)

      const vaultDecimals = decimals?.[idx] || 18
      const underlyingAsset = underlyingAssets?.[idx]

      // Special handling for WHYPE vaults: combine native HYPE + wrapped WHYPE balances
      let walletBalanceBreakdown: WalletBalanceBreakdown | undefined
      if (
        underlyingAsset?.toLowerCase() === WHYPE_ADDRESS_HYPEREVM.toLowerCase() &&
        nativeBalance &&
        chainId === E_LEND_CHAIN_ID
      ) {
        const wrappedBalance = walletBalance
        const nativeHypeBalance = nativeBalance.value

        walletBalanceBreakdown = {
          nativeBalance: nativeHypeBalance,
          nativeBalanceFormatted: formatUnits(nativeHypeBalance, vaultDecimals),
          wrappedBalance,
          wrappedBalanceFormatted: formatUnits(wrappedBalance, vaultDecimals),
        }
        walletBalance = nativeHypeBalance + wrappedBalance
      }

      // Convert sub-account balances to array with formatted values
      // Note: Vault shares are always 18 decimals in Euler, sharePrice is also 18 decimals scaled.
      // depositAssets uses vaultDecimals because it represents the underlying asset amount.
      const subAccountBalances: SubAccountBalance[] = Array.from(result.subAccountBalances.values()).map((sub) => ({
        subAccountId: sub.subAccountId,
        subAccountAddress: sub.subAccountAddress,
        vaultBalance: sub.balance,
        vaultBalanceFormatted: formatUnits(sub.balance, 18), // Shares are always 18 decimals
        depositAssets: (sub.balance * sharePrice) / BigInt(10 ** 18), // sharePrice is 18 decimals scaled
        depositAssetsFormatted: formatUnits((sub.balance * sharePrice) / BigInt(10 ** 18), vaultDecimals),
      }))

      balances.set(vaultAddress, {
        vaultAddress,
        vaultBalance,
        vaultBalanceFormatted: formatUnits(vaultBalance, 18), // Shares are always 18 decimals
        walletBalance,
        walletBalanceFormatted: formatUnits(walletBalance, vaultDecimals),
        sharePrice,
        depositAssets,
        depositAssetsFormatted: formatUnits(depositAssets, vaultDecimals),
        debt,
        debtFormatted: formatUnits(debt, vaultDecimals),
        firstSubAccountWithBalance: result.firstSubAccountWithBalance,
        subAccountBalances,
        walletBalanceBreakdown,
      })
    })
  }

  return {
    balances,
    isPending,
  }
}
